import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  DocumentViewerContainer,
  DetailsContainer,
  FieldsContainer,
  Field,
  Label,
  StatusContainer,
  DescriptionLabelContainer,
  DescriptionBlockContainer,
  UploadFilesContainer,
  SmallLabel,
  UploadedFilesContainer,
  SingleUploadedFile,
  RecordedAudioContainer,
  RecordedAudioList,
  DescriptionContainer,
} from "./styles";
import { Divider, Grid, Tooltip, Typography, useTheme } from "@mui/material";
import { AlertLineReportDTO } from "../../models/alert-line-report/alert-line-report-dto";

import { useTranslation } from "react-i18next";
import UpdateAlertLineReportStatus from "../ManageAlertLines/ManageAlertLineReportModal/UpdateAlertLineReportStatus";
import { IFile } from "../../reusable-components/DocumentViewer/interfaces";
import DialogTitle from "../DialogTitle/DialogTitle";
import DocumentViewer from "../../reusable-components/DocumentViewer/DocumentViewer";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";
import { intlFormatDateWithHour } from "../../utils/time-utils";
import FlagIcon from "../FlagIcon/FlagIcon";
import AttachedDocuments from "../AttachedDocuments/AttachedDocuments";
import { AlertLineReportFileInfoRef } from "../../models/alert-line-report/alert-line-report-file-info-ref";
import { getDownloadedFile } from "../../services/documents";
import { toast } from "react-toastify";

interface Props {
  alertLineReport: AlertLineReportDTO;
  handleUpdateStatus?: () => void;
  viewOnly?: boolean;
  forExport?: boolean;
  asReviewer?: boolean;
}

interface AudioURL {
  fileId: string;
  url: string;
}

interface GeneratedIFiLe extends IFile {
  id: string;
}

interface State {
  audioURLs: AudioURL[];
  files: GeneratedIFiLe[];
  error: string;
  loadingFiles: boolean;
  loadingFileId: string | null;
  loadingDescriptionFiles: boolean;
  fileToView: IFile | null;
  audioData: Record<string, string>;
  loading: boolean;
}

const AlertLineReportDetails = ({
  alertLineReport,
  handleUpdateStatus,
  viewOnly = false,
  forExport,
  asReviewer,
}: Props) => {
  const tRP = "components.alert_line_report_details";
  const tRPFields = "components.alert_line_report_details.fields";

  const { t } = useTranslation();
  const theme = useTheme();

  const [state, setState] = useState<State>({
    audioURLs: [],
    files: [],
    error: "",
    loadingFiles: false,
    loadingFileId: null,
    loadingDescriptionFiles: false,
    fileToView: null,
    audioData: {},
    loading: false,
  });

  const docWithVocal = useMemo(() => {
    return alertLineReport.files.find((_file) => _file.isDescription)?.document;
  }, [alertLineReport]);

  const loadAudioData = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));

    try {
      const files = docWithVocal?.files ?? [];

      const res = await Promise.all(
        files.map(async (file) => {
          return getDownloadedFile(file, true);
        })
      );

      const result: Record<string, string> = {};

      res.forEach((el, idx) => {
        if (!!el) {
          const fileId = files[idx].id;
          result[fileId] = el?.data ? URL.createObjectURL(el?.data) : "";
        }
      });

      setState((state) => ({
        ...state,
        audioData: result,
      }));
    } catch (error) {
      toast.error("Something went wrong loading vocal messages.", { autoClose: 5000 });

      console.error(error);
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, [docWithVocal]);

  useEffect(() => {
    if (!!docWithVocal?.files?.length) {
      loadAudioData();
    }
  }, [docWithVocal]);

  return (
    <>
      <DetailsContainer>
        <StatusContainer>
          <Grid style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <Field>
              <Label>{`${t(`${tRP}.report_key`)}:`}</Label>
              <Typography>{alertLineReport.key.slice(0, 6)}******</Typography>
            </Field>
          </Grid>

          <Grid style={{ display: "flex", alignItems: "center" }}>
            {handleUpdateStatus && !forExport && !viewOnly && (
              <UpdateAlertLineReportStatus
                alertLineReport={alertLineReport}
                handleUpdateParent={handleUpdateStatus}
              />
            )}
            {asReviewer && (
              <Grid>
                <Typography>{alertLineReport.alertLineReportStatus?.name ?? "Submitted"}</Typography>
                <Typography variant="body2"> in {alertLineReport.language?.name}</Typography>
              </Grid>
            )}
          </Grid>
        </StatusContainer>
        <Field>
          <Label>{`${t(`${tRPFields}.added_on`)}:`}</Label>
          <Typography>{alertLineReport.createdAt && intlFormatDateWithHour(alertLineReport.createdAt)}</Typography>
          {asReviewer && alertLineReport.countryCode && (
            <Tooltip title={alertLineReport.countrName}>
              <FlagIcon countryCode={alertLineReport.countryCode} />
            </Tooltip>
          )}
        </Field>
        <Field>
          <Label>{`${t(`${tRPFields}.addressed_to`)}:`}</Label>

          {alertLineReport.addressedToHr && (
            <Grid>
              <Typography>{`${t(`${tRPFields}.addressed_to_hr`)}`}</Typography>
            </Grid>
          )}
          {alertLineReport.addressedToHr && alertLineReport.addressedToManagement && (
            <Divider
              orientation="vertical"
              flexItem
            />
          )}
          {alertLineReport.addressedToManagement && (
            <Grid>
              <Typography>{`${t(`${tRPFields}.addressed_to_manager`)}`}</Typography>
            </Grid>
          )}
          {alertLineReport.addressedToManagement && alertLineReport.addressedToCompanyCommittee && (
            <Divider
              orientation="vertical"
              flexItem
            />
          )}
          {alertLineReport.addressedToCompanyCommittee && (
            <Grid>
              <Typography>{`${t(`${tRPFields}.addressed_to_company_committee_members`)}`}</Typography>
            </Grid>
          )}
        </Field>

        <FieldsContainer>
          <Field>
            <Label>{`${t(`${tRPFields}.subject`)}`} :</Label>
            <Typography>{alertLineReport.subject}</Typography>
          </Field>
          <Field>
            <Label>{`${t(`${tRPFields}.category`)}`} :</Label>
            <Typography>{alertLineReport.violationClassification?.name}</Typography>
          </Field>
          {!alertLineReport.anonymous && (
            <>
              <Field>
                <Label>{`${t(`${tRPFields}.full_name`)}`} :</Label>
                <Typography>{alertLineReport.fullName}</Typography>
              </Field>
              <Field>
                <Label>{`${t(`${tRPFields}.email`)}`} :</Label>
                <Typography>{alertLineReport.email}</Typography>
              </Field>
              <Field>
                <Label>{`${t(`${tRPFields}.phone_number`)}`} :</Label>
                <Typography>{alertLineReport.phoneNumber}</Typography>
              </Field>
            </>
          )}
          <DescriptionLabelContainer>
            <Label>{`${t(`${tRP}.fields.description`)}`} :</Label>
          </DescriptionLabelContainer>
          <DescriptionBlockContainer>
            <UploadFilesContainer>
              <SmallLabel>{`${t(`${tRP}.fields.files`)}`} </SmallLabel>

              {alertLineReport.files.filter((file) => !file.isDescription).length > 0 ? (
                <UploadedFilesContainer>
                  {forExport ? (
                    <Typography
                      variant="body2"
                      style={{ color: theme.palette.secondary.light, padding: "14px" }}
                    >{`${alertLineReport.files.filter((file) => !file.isDescription).length} ${t(
                      `${tRP}.fields.files_uploaded`
                    )} `}</Typography>
                  ) : (
                    alertLineReport.files
                      .filter((file) => !file.isDescription)
                      .map((_file: AlertLineReportFileInfoRef, idx) => (
                        <React.Fragment key={idx}>
                          <SingleUploadedFile>
                            {_file.document && (
                              <AttachedDocuments
                                documentIDs={[_file.document.id]}
                                count={_file.document.filesCount}
                                buttonSize="2x"
                                isGenericDocument
                                isWidget
                                documentIsReadOnly
                                allowShare={false}
                                allowDownloadContent
                                useSimpleView
                              />
                            )}
                          </SingleUploadedFile>
                        </React.Fragment>
                      ))
                  )}
                </UploadedFilesContainer>
              ) : (
                <>
                  <Typography
                    variant="body2"
                    style={{ color: theme.palette.secondary.light, padding: "14px" }}
                  >
                    <i>{`${t(`${tRP}.no_files`)}`}</i>
                  </Typography>
                </>
              )}
            </UploadFilesContainer>

            <LoadingOverlay loading={state.loading}>
              <RecordedAudioContainer>
                <SmallLabel>{`${t(`${tRP}.fields.recorded_voice_messages`)}`}</SmallLabel>
                <Grid
                  style={{ display: "flex", padding: "0px 10px", gap: "5px", alignItems: "start", alignSelf: "center" }}
                >
                  {alertLineReport.files.filter((file) => file.isDescription).length > 0 ? (
                    <Typography
                      variant="body2"
                      style={{ color: theme.palette.secondary.light, marginTop: "10px" }}
                    >
                      <i>{`${t(`${tRP}.fields.no_filters`)}`} </i>
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      style={{ color: theme.palette.secondary.light, marginTop: "10px" }}
                    >
                      <i>{`${t(`${tRP}.fields.no_recorded_messages`)}`} </i>
                    </Typography>
                  )}
                </Grid>

                <RecordedAudioList>
                  {forExport ? (
                    <Typography
                      variant="body2"
                      style={{ color: theme.palette.secondary.light, padding: "14px", alignSelf: "center" }}
                    >{`${
                      alertLineReport.files.filter((file) => file.isDescription).length > 0
                        ? `${alertLineReport.files.filter((file) => file.isDescription).length} ${t(
                            `${tRP}.fields.recorded_voice_messages`
                          )} `
                        : ""
                    }
                    `}</Typography>
                  ) : (
                    alertLineReport.files
                      .filter((file) => file.isDescription)
                      .map((file, idx) => {
                        return (
                          <React.Fragment key={idx}>
                            {file.document?.files.map((_file) => {
                              return (
                                <React.Fragment key={_file.id}>
                                  <Grid style={{ display: "flex" }}>
                                    <audio
                                      key={_file.id}
                                      controls
                                      src={_file.id ? state.audioData[_file.id] : ""}
                                      preload="auto"
                                    />
                                  </Grid>
                                </React.Fragment>
                              );
                            })}
                          </React.Fragment>
                        );
                      })
                  )}
                </RecordedAudioList>
              </RecordedAudioContainer>
            </LoadingOverlay>

            <DescriptionContainer>
              <Typography>{alertLineReport.description}</Typography>
            </DescriptionContainer>
          </DescriptionBlockContainer>
        </FieldsContainer>
      </DetailsContainer>
      {state.fileToView && (
        <DocumentViewerContainer open={!!state.fileToView}>
          <DialogTitle
            title=""
            handleClose={() => setState((state) => ({ ...state, fileToView: null }))}
          />
          <Grid>
            {state.fileToView && (
              <DocumentViewer
                file={state.fileToView}
                withFooter
              />
            )}
          </Grid>
        </DocumentViewerContainer>
      )}
    </>
  );
};
export default AlertLineReportDetails;
